/*
* HiFive Music React Music Player
* 2018 Alec Day
*/
import React, { Component, useEffect, useRef } from 'react';
import _ from 'lodash';
import { inject } from 'mobx-react';
import config from 'client/shared/config';
import autobind from 'autobind-decorator';
import shaka from 'shaka-player';

import PlaybackControls from './playback-controls';
import { 
    TimeDisplay, 
    AuxControls, 
    TrackList, 
    FavButton, 
    ProgressBar 
} from './helper-controls';

function getRandomInt(max) {
    return Math.floor(Math.random() * Math.floor(max));
}

function clamp(num, min, max) {
    return Math.min(Math.max(num, min), max);
}

//Fisher-Yates shuffle for new songList to feed
function shuffleList(list) {
    var shuffled = list;
    for (var i = shuffled.length - 1; i > 0; i--) {
        var j = getRandomInt(i + 1);
        var temp = shuffled[i];
        shuffled[i] = shuffled[j];
        shuffled[j] = temp;
    }

    return shuffled;
}

@inject('userStore', 'apiStore', 'eventStore')
class HFPlayer extends Component {
    constructor(props) {
        super(props);

        const {trackdata} = props;
        
        this.state = {
            playing: false, 
            currentTrackIndex: 0, 
            favorited: false, 
            repeatState: 0, 
            shuffle: false, 
            activeList: this.props.songList,
            audioState: { dur: 0, ela: 0, vol: 100 }, 
            trackdata: trackdata, 
            scroll: 0,
            listenEventSent: false,
            volume: [0, 100]
        };

        // Handles direct manipulation of the <audio> element (e.g., play, pause, seek).
        this.audioRef = React.createRef();

        // Manages advanced playback features, adaptive streaming, request management and DRM provided by shaka.js.
        this.playerRef = React.createRef();

        // Manages the current track index.
        this.currentTrackIndexRef = React.createRef();
    }

    componentDidMount() {
        // Install polyfills
        shaka.polyfill.installAll();

        // Check browser support
        if (shaka.Player.isBrowserSupported()) {

            // Create Shaka player instance
            const player = new shaka.Player(this.audioRef.current);
            player.getNetworkingEngine().registerRequestFilter(function(type, request) {
                request.allowCrossSiteCredentials = true;
            });
            shaka.net.NetworkingEngine.registerScheme('https', (uri, request, requestType, progressUpdated) => {
                return shaka.net.HttpXHRPlugin(uri, request, requestType, progressUpdated, {
                    // This ensures authentication cookies are sent with the requests
                    withCredentials: true
                });
            });

            this.playerRef.current = player;

            // Add error event listener
            this.playerRef.current.addEventListener('error', (event) => {
                console.error('Error code', event.detail.code, 'object', event.detail);
            });

            // Cleanup on unmount
            this.cleanup = () => {
                this.playerRef.current.destroy();
            };
        }

        this.audioRef.current.addEventListener("timeupdate", this.handleTimeUpdate);
        this.audioRef.current.addEventListener("ended", this.handleSongEnded);

        this.setState((state, props) => {
            return {
                audioState: { dur: 0, ela: 0, vol: state.volume / 100 },
            }
        });
        // const scrollPosition = sessionStorage.getItem("scrollPosition");
        // if (scrollPosition) {
        //     window.scrollTo(0, parseInt(scrollPosition));
        //     sessionStorage.removeItem("scrollPosition");
        // }
    }

    componentWillReceiveProps(newProps) {
        if (newProps.trackdata === this.props.trackdata) {

            return;
        }

        console.log("New Props?!?", newProps);

        this.setState({
            trackdata: newProps.data,
            scroll: window.pageYOffset,
            activeList: newProps.songList,
            favorited: false,
            listenEventSent: false
        }, () => this.playAudio(true));
    }
    
    componentDidUpdate (prevProps, prevState, snapshot) {
        // const scrollPosition = sessionStorage.getItem("scrollPosition");
        // if (scrollPosition) {
        //     window.scrollTo(0, parseInt(scrollPosition));
        // }
    }

    @autobind
    handleVolumeChange(value) {
        this.setState({ volume: value }, () => {
            this.audioRef.current.volume = (value[1] / 100.0);
            this.setState((state, props) => {
                return {
                    audioState: { 
                        dur: this.audioRef.current.duration, 
                        ela: this.audioRef.current.currentTime, 
                        vol: value[1] / 100.0 
                    }
                }
            });
        });
    }

    @autobind
    handleTimeUpdate(e) {
        e.preventDefault();

        const { duration, currentTime } = this.audioRef.current;

        // console.log('currentTime', currentTime, config.rules.MAX_SONG_LISTEN_EVENT_DURATION_SECONDS, this.state.listenEventSent);

        if (currentTime > config.rules.MAX_SONG_LISTEN_EVENT_DURATION_SECONDS && !this.state.listenEventSent) {
            this.props.eventStore.listenToSong(this.state.activeList[0].id);
            this.setState((state, props) => {
                return {
                    listenEventSent: true
                }
            });
        }

        this.setState((state, props) => {
            return {
                audioState: { dur: duration, ela: currentTime }
            }
        });
    }

    @autobind
    handleSongEnded(e) {
        e.preventDefault();
        this.setState((state, props) => {
            let nextTrackIndex = (state.shuffle) ? getRandomInt(this.props.trackdata.tracks.length) : state.currentTrackIndex + 1;

            if (nextTrackIndex > this.props.trackdata.tracks.length - 1) {
                //Repeat All
                if (state.repeatState === 1) {
                    nextTrackIndex = 0;
                    return {
                        currentTrackIndex: nextTrackIndex
                    }
                }

                return {
                    playing: false
                }
            }
            return {
                currentTrackIndex: nextTrackIndex
            }
        }, () => this.playAudio(true, (this.state.repeatState < 1 && this.state.currentTrackIndex + 1 > this.props.trackdata.tracks.length - 1)));
    }

    @autobind
    handleSeek(e) {
        e.preventDefault();
        var bounds = e.target.getBoundingClientRect();
        var clickSpot = (e.clientX - bounds.left) / bounds.width;
        var newPercent = clamp(clickSpot, 0, 1);

        //Seek to spot
        if (this.state.audioState.dur > 0) {
            this.audioRef.current.currentTime = (newPercent * this.state.audioState.dur);
            return {
                audioState: { ela: this.audioRef.current.currentTime }
            }
        }
    }

    @autobind
    async favoriteSong() {
        const { userStore, eventStore } = this.props;
        const song = this.state.activeList[0];
        const user = userStore.getLoggedInUser();
        
        const hiFiveData = await eventStore.favoriteSong(song.id);
        console.log(hiFiveData);
        user.hiFived.push(hiFiveData);

        this.setState(() => {
            return {
                favorited: true
            }
        });
    }

    @autobind
    async unfavoriteSong() {
        const { userStore, apiStore } = this.props;
        const song = this.state.activeList[0];
		const user = userStore.getLoggedInUser();
		const { id } = _(user.hiFived).filter({ itemID: song.id }).first();
		const route = `${config.getRoute('hiFive')}/${id}`;
		apiStore.delete({ route });
        user.hiFived = _.remove(user.hiFived.slice(), { id });
        
        this.setState(() => {
            return { favorited: false }
        });
    }

    @autobind
    handlePlayClick() {
        this.setState((state, props) => {
            let currentTrackIndex = state.currentTrackIndex;
            if (currentTrackIndex === -1) {
                currentTrackIndex = 0;
            }
            return {
                playing: true,
                currentTrackIndex: currentTrackIndex,
                audioState: { dur: this.audioRef.current.duration }
            };
        }, this.playAudio(false));
    }

    @autobind
    handlePauseClick() {
        this.setState((state, props) => {
            return {
                playing: false,
            };
        }, this.pauseAudio);
    }

    @autobind
    handlePreviousClick() {
        const songRestartThreshold = 3.5;
        this.setState((state, props) => {
            let prevTrackIndex = state.currentTrackIndex - 1;

            if (this.state.audioState.ela > songRestartThreshold) {
                this.audioRef.current.currentTime = (0 * this.state.audioState.dur);
                return {
                    playing: true,
                    audioState: { ela: this.audioRef.current.currentTime }
                }
            } else if (prevTrackIndex < 0) {
                return null;
            } else {
                return {
                    playing: true,
                    currentTrackIndex: prevTrackIndex,
                    audioState: { dur: this.audioRef.current.duration }
                }
            }
        }, () => this.playAudio(true, this.state.currentTrackIndex - 1 < -1));
    }

    @autobind
    handleNextClick() {
        this.setState((state, props) => {
            let nextTrackIndex = state.currentTrackIndex + 1;

            if (nextTrackIndex > state.activeList.length - 1) {
                //Repeat All
                if (state.repeatState === 1) {
                    nextTrackIndex = 0;
                    return {
                        currentTrackIndex: nextTrackIndex
                    }
                }
                return null;
            } else {
                return {
                    playing: true,
                    currentTrackIndex: nextTrackIndex,
                    audioState: { dur: this.audioRef.current.duration }
                }
            }
        }, () => this.playAudio(true, (this.state.repeatState < 1 && this.state.currentTrackIndex + 1 > this.props.trackdata.tracks.length - 1)));
    }

    @autobind
    handleHifiveClick() {
        console.log("Track was Favorited");

        if(!this.state.favorited) {
            this.favoriteSong();
        } else { 
            this.unfavoriteSong();
        }

        console.log(this.state.favorited);
    }

    @autobind
    handleShuffleClick() {
        const ls = shuffleList(this.state.activeList);
        console.log(ls);
        this.setState((state, props) => {
            return {
                shuffle: !state.shuffle,
                activeList: (!state.shuffle) ? ls : state.activeList
            }
        });
    }

    @autobind
    handleRepeatClick() {
        this.setState((state, props) => {
            let repeatState = state.repeatState + 1;
            if (repeatState > 2) {
                repeatState = 0;
            }
            return {
                repeatState: repeatState
            }
        });
    }

    @autobind
    handleClick(evt) {
        evt.preventDefault();
        const id = evt.target.id;
        const camelCaseId = id.charAt(0).toUpperCase() + id.slice(1);
        this[`handle${camelCaseId}Click`]?.();
    }

    @autobind
    playAudio(isSongChanged, isEndOfList) {
        if (isEndOfList) {
            return null;
        }

        // Changed streamFile to previewFile
        if (isSongChanged) {
            const activeList = this.state.activeList,
                trackdata = this.props.trackdata;
            
            let index = this.state.currentTrackIndex,
                changedSong = activeList[index];

            if(!_.isArray(activeList) && !_.isObject(activeList)) {
                index = 0;
                changedSong = [activeList];
            }

            this.audioRef.current.src = `${trackdata?.tracks?.streamPrefix}${activeList[index]?.previewFile}`;
            this.audioRef.current.load();
            this.props.onSongChange(changedSong);

            // Check if the song is favorited
            let isFavorited = false;
        
            try {
                const user = this.props.userStore.getLoggedInUser();
                const [song] = this.props.songList;
                
                if (song) {
                    isFavorited = user.hiFived.filter((a) => a.itemID===song.id).length > 0;
                }
            } catch (error) {
                console.error("Error in componentWillReceiveProps. Could not find current song to apply favorited check.", error);
            }
            
            this.setState((state, props) => {
                return {
                    playing: true,
                    favorited: isFavorited,
                    audioState: { dur: this.audioRef.current.duration }
                }
            });
        }

        this.audioRef.current.play();
    }

    @autobind
    pauseAudio() {
        this.setState((state, props) => {
            return {
                playing: false
            }
        });
        this.audioRef.current.pause();
    }

    @autobind
    selectTrackNumber(trackId) {
        this.setState((state, props) => {
            return {
                playing: true,
                currentTrackIndex: trackId,
                audioState: { dur: this.audioRef.current.duration }
            }
        }, this.playAudio(true))
    }

    render() {

        return (
            <div className="HFPlayer">
                <div
                    className="Artwork"
                    style={{ backgroundImage: "url(" + this.props.trackdata.tracks.artwork + ")" }}
                >

                </div>
                <div className="TrackList">
                    <TrackList data={this.props.trackdata} songList={this.state.activeList} currentTrackIndex={this.state.currentTrackIndex} isShuffled={this.shuffle} currentList={this.state.activeList} />
                    <div className="Favorite">
                        <FavButton
                            tracks={this.props.trackdata.tracks}
                            onClick={this.handleClick}
                            isPlaying={this.state.playing}
                            isFavorited={this.state.favorited}
                        />
                    </div>
                </div>
                
                <div className="Controls">
                    <PlaybackControls onClick={this.handleClick.bind(this)} playing={this.state.playing} repeatType={this.state.repeatState} shuffle={this.state.shuffle} volume={this.state.audioState.vol} />
                    <audio 
                        ref={this.audioRef}
                        className="audioSrc" 
                        preload="auto" 
                        crossOrigin={"anonymous"} 
                        loop={this.state.repeatState === 2}
                    >
                        <source src='' type={"audio/mp3"}/>
                    </audio>
                </div>
                <div className="Progress">
                    <ProgressBar ref="Progress0" seekEvent={this.handleSeek} elapsedTime={this.state.audioState.ela} trackDuration={this.state.audioState.dur} />
                </div>
                <div className="ControlGroup">
                    <TimeDisplay elapsed={this.state.audioState.ela} duration={this.state.audioState.dur} />
                    <div className="AuxControls">
                        <AuxControls 
                            onClick={this.handleClick} 
                            onChange={this.handleVolumeChange} 
                            volume={this.state.volume}
                            onComplete={this.handleVolumeChange} 
                        />
                    </div>
                </div>
            </div>
        );
    }

    componentWillUnmount() {
        if (this.cleanup) {
            this.cleanup();
        }
    }
}

export default HFPlayer;